import { useScrollReveal } from "@/hooks/useScrollReveal";
import { lean } from "@/content";
import { Rich } from "@/lib/rich";
import MediaImage from "@/components/media/MediaImage";

export default function LeanHero() {
  const { ref, revealed } = useScrollReveal(0.1);

  return (
    <section className="relative min-h-[600px] md:min-h-[720px] flex items-center overflow-hidden pt-20">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-stone-50 to-[#FAFAF8] dark:from-[#0C0C0E] dark:via-[#0A0A0A] dark:to-[#0C0C0E]" />
      <div className="absolute top-[10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-orange-200/30 blur-[140px] dark:bg-orange-500/15 pointer-events-none" />
      <div className="absolute bottom-[5%] left-[-10%] w-[400px] h-[400px] rounded-full bg-amber-100/40 blur-[120px] dark:bg-amber-500/10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left: Text */}
          <div
            ref={ref}
            className={`flex-1 text-center lg:text-left transition-all duration-700 ${
              revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 dark:bg-orange-500/[0.12] dark:border-orange-400/25 dark:text-orange-300 text-xs font-semibold tracking-widest uppercase mb-6">
              {lean.hero.badge}
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-stone-900 leading-tight dark:text-white mb-6">
              <Rich text={lean.hero.title} accentClassName="text-orange-600 dark:text-orange-500" />
            </h1>
            <p className="text-stone-600 text-lg leading-relaxed mb-8 max-w-xl dark:text-white/60 mx-auto lg:mx-0">
              {lean.hero.lead}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a
                href="#contact"
                className="px-8 py-4 rounded-2xl bg-orange-600 text-white font-bold text-sm hover:bg-orange-700 dark:bg-orange-500 dark:text-[#0A0A0A] dark:hover:bg-orange-400 transition-colors cursor-pointer whitespace-nowrap"
              >{lean.hero.primaryCta.label}</a>
              <a
                href="#pricing"
                className="px-8 py-4 rounded-2xl border border-stone-300 text-stone-700 font-bold text-sm hover:bg-stone-100 dark:border-white/20 dark:text-white dark:hover:bg-white/[0.06] transition-colors cursor-pointer whitespace-nowrap"
              >{lean.hero.secondaryCta.label}</a>
            </div>
          </div>

          {/* Right: Visual */}
          <div className="flex-1 w-full max-w-lg">
            <MediaImage
              image={{
                src: lean.hero.image,
                alt: lean.hero.imageAlt,
                aspect: lean.hero.imageAspect ?? "4/3",
                caption: lean.hero.imageCaption,
              }}
              imgClassName="object-top"
              priority
            />
          </div>
        </div>
      </div>
    </section>
  );
}